import type { FramingProfileDescriptor } from "building-types/framing/framingProfileDescriptorTypes";
import type { HeaderAssemblyId } from "building-types/framing/headerAssemblyTypes";
import type { LumberProfileDescriptor, LumberRail } from "building-types/framing/lumberTypes";
import type { OpeningHandle, RoomEdgeHandle, RoomHandle } from "building-types/handles/docEntityHandleTypes";
import type { ForeignEntityId } from "building-types/handles/foreignEntityId";
import type { WallFramingOpeningSettings } from "building-types/settings/wallFramingOpeningSettings";
import type { WallCondition } from "building-types/walls/wallType";
import type { Table } from "common/data/table";
import type { Plane } from "geom/planeTypes";
import type { WallFramingMetadata } from "../walls/wallFramingMetaDataTypes";
import type { FramingBoundaryWithMetadata, FramingConfig } from "./framingMetaDataTypes";

export enum WallFramingType {
  STANDARD_STUD = "STANDARD_STUD",
  KING_STUD = "KING_STUD",
  JACK_STUD = "JACK_STUD",
  BOTTOM_CRIPPLE_STUD = "BOTTOM_CRIPPLE_STUD",
  TOP_CRIPPLE_STUD = "TOP_CRIPPLE_STUD",
  TOP_PLATE = "TOP_PLATE",
  BOTTOM_PLATE = "BOTTOM_PLATE",
  BOTTOM_SILL_PLATE = "BOTTOM_SILL_PLATE",
  BLOCKING = "BLOCKING",
  HEADER = "HEADER",
  TOP_SILL_PLATE = "TOP_SILL_PLATE",
}

/** Perimeter corner used as the stud layout origin (`framingMetadata.framingStartPt`). */
export enum WallFramingStartCorner {
  BOTTOM_LEFT = "BOTTOM_LEFT",
  BOTTOM_RIGHT = "BOTTOM_RIGHT",
  TOP_LEFT = "TOP_LEFT",
  TOP_RIGHT = "TOP_RIGHT",
}

export enum OpeningSource {
  DOOR = "DOOR",
  WINDOW = "WINDOW",
}

export enum UnionType {
  OR = "OR",
  AND = "AND",
}

export type AdjacentRooms = [RoomHandle | null, RoomHandle | null]; //first is the source, second is the adjacent or null if no adjacent

export interface ResolvedOpeningFraming {
  /** stable opening id this resolution applies to. */
  readonly openingId: string;
  /** `null` when the opening explicitly disables its header; otherwise the resolved assembly id. */
  readonly headerAssemblyId: HeaderAssemblyId | null | undefined;
  readonly headerAssemblyName: string | undefined;
  readonly headerComponentId: ForeignEntityId<"FRAMING_COMPONENT"> | undefined;
  /**
   * When true, the opening is omitted from framing generation and treated as part of the wall region
   * (studs and plates run through continuously). Not yet wired from doc settings; always false from
   * the wall-plane pipeline until opening framing can be disabled end-to-end.
   */
  readonly framingDisabled: boolean;
  readonly numberOfJackStuds: number;
  readonly numberOfKingStuds: number;
  readonly roughOpeningBorder: number;
  readonly headerCount: number;
  readonly hasCrippleStudEnds: boolean;
  readonly hasCrippleStudsBelowOpening: boolean;
  readonly hasCrippleStudsAboveOpening: boolean;
  readonly hasTopHeaderSillPlate: boolean;
}

/** Runtime-resolved opening framing keyed by stable opening id. */
export type ResolvedOpeningFramingTable = Table<ResolvedOpeningFraming>;

export interface ResolvedRoughOpeningFramingSettings extends ResolvedOpeningFraming {
  readonly openingSettings: WallFramingOpeningSettings;
  readonly gapLength: number;
  readonly headerProfile: LumberProfileDescriptor;
}

export interface WallFramingMeta {
  readonly adjacentRooms?: AdjacentRooms;
  readonly tag?: string;
  readonly wallHeight?: number;
  readonly spacing?: number;
  readonly wallCondition?: WallCondition;
  readonly source?: OpeningSource;
  readonly sourceHandle?: RoomEdgeHandle;
  readonly openingHandle: OpeningHandle | undefined;
  readonly headerAssemblyName?: string;
  readonly headerAssemblyId?: HeaderAssemblyId | null;
  readonly resolvedOpeningFraming?: ResolvedOpeningFraming;
  /**
   * stable opening ids this member belongs to. Most members reference a single opening; shared members
   * (e.g. king/cripple studs between vertically stacked openings) reference more than one.
   */
  readonly openingIds?: readonly string[];
}

export interface WallFramingBase<T extends WallFramingType> {
  readonly id: string;
  readonly profileDescriptor: FramingProfileDescriptor;
  readonly rails: LumberRail[];
  readonly type: T;
  readonly meta?: WallFramingMeta;
  /**
   * Shared id linking the depth-stacked plies of one physical header (HEADER members only). Used by
   * the output conversion to merge a header's plies into a single output member holding all plies as
   * rails; dropped when adapting to the building output type.
   */
  readonly headerGroupId?: string;
}

export type Header = WallFramingBase<WallFramingType.HEADER>;

export type Plate = WallFramingBase<
  | WallFramingType.BOTTOM_PLATE
  | WallFramingType.TOP_PLATE
  | WallFramingType.BOTTOM_SILL_PLATE
  | WallFramingType.TOP_SILL_PLATE
>;
export type Stud = WallFramingBase<
  | WallFramingType.STANDARD_STUD
  | WallFramingType.KING_STUD
  | WallFramingType.JACK_STUD
  | WallFramingType.BOTTOM_CRIPPLE_STUD
  | WallFramingType.TOP_CRIPPLE_STUD
>;
export type Blocking = WallFramingBase<WallFramingType.BLOCKING>;
export type Framing = Plate | Stud | Header | Blocking;

export interface RoughOpeningConfig {
  readonly headerType: OpeningSource | undefined;
  readonly start: number;
  readonly end: number;
  readonly extendedStart: number;
  readonly extendedEnd: number;
  readonly bottom: number;
  readonly top: number;
  readonly sillPlateCount: number;
  readonly headerProfile: LumberProfileDescriptor;
  readonly headerAssemblyName?: string;
  readonly headerAssemblyId?: HeaderAssemblyId | null;
  readonly numberOfJackStuds: number;
  readonly numberOfKingStuds: number;
  readonly headerCount: number;
  readonly resolvedOpeningFraming: ResolvedOpeningFraming;
  readonly hasCrippleStudEnds: boolean;
  readonly hasCrippleStudsBelowOpening: boolean;
  readonly hasCrippleStudsAboveOpening: boolean;
  readonly hasTopHeaderSillPlate: boolean;
  readonly adjacentRooms: AdjacentRooms;
  readonly openingHandle: OpeningHandle | undefined;
}

export const DEFAULT_STUD_DISTANCE = 16;
export const MIN_FRAMING_STUD_LENGTH = 1.5;

export interface GeneratedFramedWallAssembly /*extends BuildingEntityBase<"FRAMED_WALL_ASSEMBLY">*/ {
  readonly levelId: string;
  readonly framing: Framing[];
  readonly openingFramingErrors?: readonly string[];
}

export type HeaderConfig = FramingConfig;
export type KingStudConfig = FramingConfig;
export type JackStudConfig = FramingConfig;
export type SillPlateConfig = FramingConfig;
export type HeaderSillConfig = FramingConfig;
export type CrippleStudConfig = Omit<FramingConfig, "count">;

export interface WallOpeningFramingConfigGroup {
  readonly headerConfig: HeaderConfig;
  readonly kingStudConfig: KingStudConfig;
  readonly jackStudConfig: JackStudConfig;
  readonly sillPlateConfig: SillPlateConfig;
  /**
   * Plate below the header (same span as header, sill-plate orientation). Omitted means disabled
   * (legacy default); enable via opening settings `hasTopHeaderSillPlate`.
   */
  readonly headerSillConfig?: HeaderSillConfig;
  readonly topCrippleStudConfig?: CrippleStudConfig;
  readonly bottomCrippleStudConfig?: CrippleStudConfig;
  /** When false, field bottom cripples span the full RO width and jamb cripples are omitted. */
  readonly hasCrippleStudEnds?: boolean;
  /**
   * Clearance (inches) added around the opening to form the rough opening: the opening bounds are
   * grown by this on all four sides before laying out jacks/kings/sill/header (mirrors legacy
   * `makeRoughOpening`). Defaults to 0 (frame tight to the opening) when omitted.
   */
  readonly roughOpeningBorder?: number;
  /**
   * When true, the opening is omitted from framing generation (debug / direct {@link WallFramingInput}
   * only until doc settings expose this). Wall-plane generation maps resolved settings here later.
   */
  readonly framingDisabled?: boolean;
}

/**
 * Width-aware opening framing resolver passed into the wall framing generator. Given an opening id and
 * its runtime-resolved rough width, returns the framing config the generator understands. Header
 * assembly resolution (which depends on the runtime width, e.g. merged width for overlapping openings)
 * happens inside the implementation; `headerAssembly` never reaches the generator (intentional).
 */
export type WallOpeningFramingResolver = (args: {
  readonly openingId: string;
  readonly roughWidth: number;
}) => WallOpeningFramingConfigGroup | undefined;

/** Default clearance (inches) between a lower header top and upper sill bottom to merge stacked opening AABBs. */
export const DEFAULT_STACKED_OPENING_HEADER_SILL_MERGE_CLEARANCE_INCHES = 2;

/** Opening boundary with optional framing config override (falls back to {@link WallFramingInput.openingFramingConfigGroup}). */
export interface WallOpeningBoundary extends FramingBoundaryWithMetadata {
  readonly openingFramingConfigGroup?: WallOpeningFramingConfigGroup;
  /**
   * Stable opening id for this boundary (can bederived from the wall-cut source). Used to key the
   * width-aware framing resolver and to tag generated members; survives convex-split filtering.
   */
  readonly openingId?: string;
}

export interface WallFramingInput {
  readonly perimeter: FramingBoundaryWithMetadata; //primeter CCW
  readonly framingMetadata: WallFramingMetadata;
  readonly openings?: readonly WallOpeningBoundary[]; //openings CW
  /** Default opening framing when an opening has no {@link WallOpeningBoundary.openingFramingConfigGroup}. */
  readonly openingFramingConfigGroup?: WallOpeningFramingConfigGroup;
  /**
   * When stacked openings share horizontal framing extent (outside king to outside king),
   * merge their parallel AABB if the gap from the lower header top to the upper sill bottom
   * is at most this distance (inches). Defaults to 2".
   */
  readonly stackedOpeningHeaderSillMergeClearance?: number;
  /**
   * When true, openings in the same horizontal band (vertically aligned / perp-overlapping)
   * use the maximum jack and king counts from all openings in that band for kings, jacks,
   * framing extents, stud suppression, and cripple layout. Defaults to false (opt-in).
   */
  readonly mergeAlignedOpeningJackKingCounts?: boolean;
  /**
   * Target 3D plane the generated framing is embedded onto. The 2D layout is unchanged: perimeter
   * and opening coordinates follow this plane's UV (2D `x` = plane `U`, 2D `y` = plane `V`), and the
   * perimeter is CCW when viewed from `+planeNormal`. When omitted, framing stays on the default
   * vertical wall plane (`planeNewXZ(0, 0, elevation)`) and `framingMetadata.elevation` applies as
   * before. When provided, this plane is the absolute target placement and `elevation` is ignored.
   */
  readonly framingPlane?: Plane;
  /**
   * Distance (inches) to offset the entire assembly along `+planeNormal` of {@link framingPlane}
   * (or the default wall plane normal when omitted). Positive moves toward the plane normal.
   */
  readonly framingPlaneNormalOffset?: number;
  /**
   * Optional width-aware opening framing resolver. When provided, per-opening framing config is
   * resolved from this (using each opening's `openingId` and runtime rough width) instead of the
   * static `openingFramingConfigGroup`. Keeps header-assembly resolution out of the generator.
   */
  readonly resolveOpeningFramingConfig?: WallOpeningFramingResolver;
}

/**
 * Test case for wall framing debug page
 */
export interface WallFramingTestCase {
  /** Stable key for v2 baselines and lookups. Never rename after baselines exist. */
  readonly id: string;
  readonly name: string;
  readonly description?: string;
  readonly input: WallFramingInput;
}
